import { useEffect } from "react";

import { create } from "zustand";

import { useUserData } from "Flocknote/apiHooks";

import { useGetSignupsList } from "NavBar/SignupsAndEvents/API/queries";

const useSignupsListPageStore = create((set) => ({
  selectedOwnerFilter: 1, // 1 for "My Signups", 2 for "All Signups"
  setSelectedOwnerFilter: (newSelectedOwnerFilter) =>
    set({ selectedOwnerFilter: newSelectedOwnerFilter }),
  selectedStatusFilter: 1, // 1 for "Active", 4 for "Archived"
  setSelectedStatusFilter: (newSelectedStatusFilter) =>
    set({ selectedStatusFilter: newSelectedStatusFilter }),
  isInitRef: { current: false },
}));

/**
 * Inits the owner filter with smart defaults based on whether user has "My Signups".
 * This should be called in SignupsList (NavBar) for early initialization and
 * optionally in SignupsListAndArchivePage as a safety net.
 */
const useInitSignupsListPageStore = () => {
  const [{ userID }] = useUserData();
  const { data, isFetched } = useGetSignupsList();
  const setSelectedOwnerFilter = useSignupsListPageStore(
    (s) => s.setSelectedOwnerFilter
  );
  const isInitRef = useSignupsListPageStore((s) => s.isInitRef);

  useEffect(() => {
    if (isInitRef.current) return;
    if (!isFetched || !data?.signupLists) return;

    isInitRef.current = true;

    // Check if user has any active signups they created
    const myActiveSignups = data.signupLists.filter(
      (signup) =>
        signup.createdBy === userID && signup.signupListStatusID === 1
    );

    // If no "My Signups", default to "All Signups"
    if (myActiveSignups.length === 0) {
      setSelectedOwnerFilter(2);
    }
    // Otherwise keep default of 1 ("My Signups")
  }, [data, isFetched, userID, setSelectedOwnerFilter, isInitRef]);
};

const useOwnerFilter = () => {
  const selectedOwnerFilter = useSignupsListPageStore(
    (s) => s.selectedOwnerFilter
  );
  const setSelectedOwnerFilter = useSignupsListPageStore(
    (s) => s.setSelectedOwnerFilter
  );

  return [selectedOwnerFilter, setSelectedOwnerFilter];
};

const useStatusFilter = () => {
  const selectedStatusFilter = useSignupsListPageStore(
    (s) => s.selectedStatusFilter
  );
  const setSelectedStatusFilter = useSignupsListPageStore(
    (s) => s.setSelectedStatusFilter
  );

  return [selectedStatusFilter, setSelectedStatusFilter];
};

export {
  useInitSignupsListPageStore,
  useOwnerFilter,
  useStatusFilter,
};

export default useSignupsListPageStore;
